// Картинки пустых состояний. Десять штук, и двенадцатой просто так не появляется.
//
// ## Зачем они
//
// Пустой экран без картинки и без причины человек читает как поломку. Иллюстрация тут не
// украшение: она за долю секунды говорит «здесь всё в порядке, просто пока пусто» — до того,
// как он начнёт читать текст. До 03.09.2026 в кабинете на всех восьмидесяти пустых состояниях
// стоял один и тот же серый значок в кружке.
//
// ## Граница жёсткая: картинка только там, где содержимого ЕЩЁ НЕ ПОЯВИЛОСЬ
//
// «Под этот отбор ничего нет», «объект закрыт», «не удалось загрузить» — это ДРУГИЕ состояния,
// и у них остаётся значок. Картинка там врёт: она успокаивает, а человеку надо снять фильтр,
// сменить месяц или повторить запрос. Поехала доля экранов с картинкой вверх — значит кто-то
// поставил её туда, где её быть не должно.
//
// ## Почему набор закрытый
//
// Из тридцати четырёх картинок набора взято десять — те, что говорят о НАШЕМ предмете.
// Биржевые сводки, кредитный рейтинг и подписки на музыку охраннику ни к чему, а лишняя
// картинка однажды окажется на экране просто потому, что была под рукой. Ровно так на телефоне
// до 29.08.2026 на «Чатов пока нет» стоял калькулятор с монетами: человек читал его как
// «здесь про деньги», открывал и не находил денег.
//
// Имена и смыслы совпадают с телефоном (`ios/scripts/import-illustrations.py`) — там, где
// смысл тот же, картинка обязана быть та же: человек ходит и в кабинет, и в приложение.

import { IllustrationДеньги } from './finance-major-expenses';
import { IllustrationЗаявки } from './finance-saved-actions';
import { IllustrationРасход } from './finance-budget-overview';
import { IllustrationПериод } from './finance-spending-summary';
import { IllustrationОбмен } from './finance-exchange';
import { IllustrationЛюди } from './hr-employee-spotlight-overview';
import { IllustrationСпокойно } from './hr-status-tracker';
import { IllustrationПереписка } from './hr-employee-comments';
import { IllustrationЗаметки } from './hr-notes';
import { IllustrationПомощник } from './hr-current-project';

/** Что нарисовано. Новое пустое состояние — существующее имя, а не двенадцатая картинка. */
export type ИмяКартинки =
  /** Денег под отчётом ни на ком нет, операций не было: пустой кошелёк. */
  | 'деньги'
  /** Заявок и чеков не подавали: планшет с закладкой. */
  | 'заявки'
  /** Расхода за период нет: счёт и разбивка по статьям. */
  | 'расход'
  /** За выбранный месяц считать нечего: сводка. */
  | 'период'
  /** Движений между сторонами не было: обмен. */
  | 'обмен'
  /** В реестре пока никого: портрет человека. */
  | 'люди'
  /** Ничего не ждёт решения: рабочее место, монитор и чашка. */
  | 'спокойно'
  /** Переписок нет: два облачка разговора. */
  | 'переписка'
  /** Записей нет: чистый блокнот. */
  | 'заметки'
  /** Ещё ни о чём не спрашивали: планшет с лампочкой. */
  | 'помощник';

const КАРТИНКИ: Readonly<Record<ИмяКартинки, (props: { className?: string }) => JSX.Element>> = {
  деньги: IllustrationДеньги,
  заявки: IllustrationЗаявки,
  расход: IllustrationРасход,
  период: IllustrationПериод,
  обмен: IllustrationОбмен,
  люди: IllustrationЛюди,
  спокойно: IllustrationСпокойно,
  переписка: IllustrationПереписка,
  заметки: IllustrationЗаметки,
  помощник: IllustrationПомощник,
};

/**
 * Картинка пустого состояния.
 *
 * Размер один на все места: 128 точек. Разный размер у равнозначных картинок читается как разная
 * важность — та же беда, из-за которой в продукте завели единственную подписанную величину.
 *
 * Сто двадцать восемь, а не девяносто шесть: набор нарисован тонкой линией в один пиксель, и на
 * широком мониторе меньший размер превращается в серое пятно — картинка перестаёт что-либо
 * говорить и остаётся просто украшением. В узкой колонке списка 128 занимают треть ширины, и это
 * нормально: пустой список читают один раз.
 */
export function Illustration({ имя }: { имя: ИмяКартинки }) {
  const Картинка = КАРТИНКИ[имя];
  // `aria-hidden`: картинка ничего не сообщает сверх заголовка и причины, которые стоят рядом
  // словами. Читалка, проговаривающая её, добавила бы человеку шум, а не смысл.
  return <Картинка className='size-32 shrink-0' aria-hidden='true' />;
}
